/* Sztuka Wypieku - podgląd 3D tortu (three.js) dla konfiguratora.
   Ładuje master GLB (assets/sw-cake.glb, generowany skryptem Blendera
   w repo aplikacji: blender/build_cake_glb.py) i mapuje stan konfiguratora
   (CustomEvent "cake:state" z bloku Cake Wizard) na widoczność grup,
   materiały, skalę i napis. Orbitowanie tak, zoom nie (wg handoffu).

   Klucze stanu = urlKey z configJson opcji:
   os(osoby) b(biszkopt) k(krem) n(nadzienia[]) ks(kształt) st(styl)
   t(topper) f(figurki) na(napis) np(pozycja napisu) nk(kolor napisu)
   nt(napis toppera) */

import * as THREE from "three";
import { GLTFLoader } from "three/gltfloader";
import { OrbitControls } from "three/orbitcontrols";

const HOST = document.querySelector("[data-sw-cake-viewer]");

function webglOk() {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl2") || c.getContext("webgl")));
  } catch (e) {
    return false;
  }
}

function showFallback(host) {
  const src = host.dataset.fallback;
  if (src) host.innerHTML = '<img src="' + src + '" alt="" style="max-width:100%;max-height:100%;object-fit:contain">';
}

if (HOST) {
  if (!webglOk()) {
    showFallback(HOST);
  } else {
    initViewer(HOST).catch(function (e) {
      console.error("[sw-cake-viewer]", e);
      showFallback(HOST);
    });
  }
}

async function initViewer(host) {
  const modelUrl = host.dataset.model;
  if (!modelUrl) throw new Error("Brak data-model");

  // ── scena / kamera / renderer ──
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 40);
  // ok. 20° nad poziomem tortu, stała odległość (bez zoomu)
  camera.position.set(2.9, 1.9, 3.6);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  /* 1.5 zamiast 2: petla renderuje bez przerwy, a gotowce podjezdzaja blizej
     kamery - przy DPR 2 zintegrowane GPU nie wyrabialo (zgloszony lag). */
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.12;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  host.innerHTML = "";
  host.appendChild(renderer.domElement);
  renderer.domElement.style.cssText = "width:100%;height:100%;display:block;outline:none";

  // ── światła: studyjne, ciepłe (~4000 K) ──
  const warm = 0xffe3c2;
  scene.add(new THREE.HemisphereLight(0xfff2e0, 0x241c12, 0.55));
  const key = new THREE.DirectionalLight(warm, 2.4);
  key.position.set(3.2, 3.4, 2.6);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xfff0dc, 0.9);
  fill.position.set(-3.4, 2.2, 2.2);
  scene.add(fill);
  const rim = new THREE.DirectionalLight(0xffe8cc, 1.4);
  rim.position.set(0.6, 3.2, -3.4);
  scene.add(rim);

  // ── kontrolki: orbit tak, zoom/pan nie ──
  /* flaga renderu na zadanie - deklarowana przed apply(null), ktore ja ustawia */
  let needsRender = true;
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableZoom = false;
  controls.enablePan = false;
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.minPolarAngle = Math.PI * 0.32;
  controls.maxPolarAngle = Math.PI * 0.46;
  controls.target.set(0, 0.42, 0);

  // ── model ──
  const gltf = await new GLTFLoader().loadAsync(modelUrl);
  const root = gltf.scene;
  scene.add(root);

  /* Standardowy tort w osobnej podgrupie: gotowe projekty z własnym GLB
     (np. „Boho ecru") chowają go jedną flagą, a napisy/teksty viewera - dodawane
     niżej wprost do roota - zostają widoczne niezależnie od podmiany modelu. */
  const stdRoot = new THREE.Group();
  stdRoot.name = "SW_Std";
  root.children.slice().forEach(function (c) { stdRoot.add(c); });
  root.add(stdRoot);

  const groups = {};
  const mats = {};
  root.traverse(function (o) {
    if (o.name && (o.name.startsWith("GRP_") || o.name === "Plate" || o.name.startsWith("Figure_"))) {
      groups[o.name] = o;
    }
    if (o.isMesh && o.material && o.material.name && !mats[o.material.name]) {
      mats[o.material.name] = o.material;
    }
  });

  /* ── złoto: mapa otoczenia ──
     Czysty metal w PBR nie ma składowej rozproszonej - odbija wyłącznie
     otoczenie. Scena ma tylko światła kierunkowe, więc płatki i perły były
     czarne i łapały złoto dopiero pod kątem odbicia. Mały proceduralny
     gradient jako mapa równoprostokątna daje metalowi stałe ciepłe refleksy
     z każdej strony. Celowo podpięty TYLKO pod materiał Gold - globalne
     scene.environment rozjaśniłoby też krem i owoce, których wygląd jest
     już zaakceptowany. */
  if (mats.Gold) {
    const ec = document.createElement("canvas");
    ec.width = 64;
    ec.height = 32;
    const ex = ec.getContext("2d");
    const eg = ex.createLinearGradient(0, 0, 0, ec.height);
    eg.addColorStop(0, "#fff3dd");      // ciepłe "niebo" nad tortem
    eg.addColorStop(0.55, "#d9b370");
    eg.addColorStop(1, "#5c4520");      // ciemniejszy "grunt"
    ex.fillStyle = eg;
    ex.fillRect(0, 0, ec.width, ec.height);
    const envTex = new THREE.CanvasTexture(ec);
    envTex.mapping = THREE.EquirectangularReflectionMapping;
    envTex.colorSpace = THREE.SRGBColorSpace;
    mats.Gold.envMap = envTex;
    mats.Gold.envMapIntensity = 1.0;
    // lekko szorstsze złoto uśrednia odbicie gradientu na drobnych płatkach -
    // przy 0,15 odbicie było lustrzane i płatki dalej migotałyby skrajnie
    mats.Gold.roughness = Math.max(mats.Gold.roughness, 0.32);
    mats.Gold.needsUpdate = true;
  }

  /* ── modele alternatywne ──
     Gotowy projekt może nieść w metaJson `model` (np. "boho") - wtedy zamiast
     standardowego tortu pokazujemy dedykowany GLB w wariancie zgodnym z liczbą
     pięter. Pliki ładują się leniwie (po ~10 MB), dopiero przy pierwszym
     wybraniu projektu; do czasu załadowania na scenie zostaje standardowy tort.
     Materiały z "cream" w nazwie (BohoCream) przemalowujemy razem z Cream,
     więc krok „Kolor tortu" działa też na podmienionym modelu. */
  const altUrls = {
    boho: { 2: host.dataset.modelBohoT2, 3: host.dataset.modelBohoT3, 4: host.dataset.modelBohoT4 },
    naked: { 2: host.dataset.modelNakedT2, 3: host.dataset.modelNakedT3, 4: host.dataset.modelNakedT4 },
    wafer: { 2: host.dataset.modelWaferT2, 3: host.dataset.modelWaferT3, 4: host.dataset.modelWaferT4 },
    elegant: { 2: host.dataset.modelElegantT2, 3: host.dataset.modelElegantT3, 4: host.dataset.modelElegantT4 },
    /* gotowce chrzcin/komunii maja warianty t1-t3 (od 19.08 do 3 pieter,
       120 osob) - zakres klamrowania pieter liczy sie z dostepnych kluczy mapy */
    chrzest: { 1: host.dataset.modelChrzestT1, 2: host.dataset.modelChrzestT2, 3: host.dataset.modelChrzestT3 },
    komunia: { 1: host.dataset.modelKomuniaT1, 2: host.dataset.modelKomuniaT2, 3: host.dataset.modelKomuniaT3 },
    pastel: { 1: host.dataset.modelPastelT1, 2: host.dataset.modelPastelT2, 3: host.dataset.modelPastelT3 },
    mis: { 1: host.dataset.modelMisT1, 2: host.dataset.modelMisT2, 3: host.dataset.modelMisT3 },
    bobas: { 1: host.dataset.modelBobasT1, 2: host.dataset.modelBobasT2, 3: host.dataset.modelBobasT3 },
    koronka: { 1: host.dataset.modelKoronkaT1, 2: host.dataset.modelKoronkaT2, 3: host.dataset.modelKoronkaT3 },
    kule: { 1: host.dataset.modelKuleT1, 2: host.dataset.modelKuleT2, 3: host.dataset.modelKuleT3 },
    kulech: { 1: host.dataset.modelKulechT1, 2: host.dataset.modelKulechT2, 3: host.dataset.modelKulechT3 },
  };
  const altCache = {};
  const altLoading = {};
  const altCreams = [];
  let altWanted = null;
  let lastState = null;
  function altKey(kind, tiers) { return kind + ":" + tiers; }
  /* Deduplikacja ladowan W LOCIE jest krytyczna: suwak rozmiaru odpala apply()
     wielokrotnie i bez niej ten sam GLB ladowalby sie rownolegle kilka razy -
     pierwszy node zostawal widoczny jako sierota poza altCache i "przyklejal"
     stary wariant na stale. */
  function ensureAlt(kind, tiers) {
    const key = altKey(kind, tiers);
    if (altCache[key]) return Promise.resolve(altCache[key]);
    if (altLoading[key]) return altLoading[key];
    const url = altUrls[kind] && altUrls[kind][tiers];
    if (!url) return Promise.resolve(null);
    altLoading[key] = new GLTFLoader().loadAsync(url).then(function (g) {
      const node = g.scene;
      node.name = "SW_Alt_" + key;
      node.visible = false;
      /* bukiet komponowany jest na froncie modelu (+Z po eksporcie) - obrót
         ustawia go w stronę domyślnej pozycji kamery (azymut ~39°) */
      node.rotation.y = THREE.MathUtils.degToRad(39);
      node.traverse(function (o) {
        if (o.isMesh && o.material && /cream/i.test(o.material.name || "")) {
          if (altCreams.indexOf(o.material) === -1) altCreams.push(o.material);
        }
      });
      root.add(node);
      altCache[key] = node;
      delete altLoading[key];
      return node;
    });
    return altLoading[key];
  }

  const TOP_Y = 0.61;                   // góra dolnego piętra (z Blendera)
  // Wysokości i promienie pięter 2-4 - tabela TIERS w blender/build_cake_glb.py.
  // Zmiana tam wymaga zmiany tutaj, inaczej topper i napis wiszą w powietrzu.
  const TIER_H = [0.451, 0.385, 0.319];
  const TIER_R = [1, 0.6, 0.38, 0.24];
  const MAX_TIERS = TIER_H.length + 1;

  /** Wysokość wierzchu tortu przy danej liczbie pięter. */
  function stackTop(tiers) {
    let y = TOP_Y;
    for (let i = 0; i < Math.min(tiers, MAX_TIERS) - 1; i++) y += TIER_H[i];
    return y;
  }

  const figureTemplates = [];
  for (let i = 0; i < 12; i++) {
    if (groups["Figure_" + i]) figureTemplates.push(groups["Figure_" + i]);
  }
  figureTemplates.forEach(function (f) { f.visible = false; });

  /* ── sylwetki kształtów ──
     Owoce, złoto, kwiaty i figurki są rozstawione w Blenderze na okrągłym
     torcie (build_cake_glb.py, jedna grupa na styl - nie po jednej na kształt).
     Przy kwadracie i sercu ten sam punkt trzeba przenieść na obrys wybranej
     bryły, inaczej dekoracja wisi obok tortu.

     Obrys mierzymy promieniami rzuconymi w model, a nie przepisując wzory z
     Blendera: wystarczy, że wszystkie trzy korpusy siedzą w tym samym GLB, i
     nie ma drugiego miejsca do synchronizowania przy zmianie geometrii. */
  const SIL_N = 128;
  const silRay = new THREE.Raycaster();
  const tmpV = new THREE.Vector3();

  function measure(name) {
    const g = groups[name];
    if (!g) return null;
    const wasVisible = g.visible;
    g.visible = true;
    root.updateMatrixWorld(true);
    const bins = new Float32Array(SIL_N);
    const from = new THREE.Vector3();
    const dir = new THREE.Vector3();
    const hitLocal = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    root.getWorldQuaternion(quat);
    for (let i = 0; i < SIL_N; i++) {
      const a = (i / SIL_N) * Math.PI * 2;
      // z zewnątrz do osi, na wysokości ścianki korpusu
      from.set(Math.cos(a) * 6, 0.35, Math.sin(a) * 6);
      dir.set(-Math.cos(a), 0, -Math.sin(a)).applyQuaternion(quat).normalize();
      silRay.set(root.localToWorld(from), dir);
      const hit = silRay.intersectObject(g, true)[0];
      if (!hit) { bins[i] = 0; continue; }
      root.worldToLocal(hitLocal.copy(hit.point));
      bins[i] = Math.hypot(hitLocal.x, hitLocal.z);
    }
    g.visible = wasVisible;
    // gdyby któryś promień chybił (zaokrąglenia), bierzemy sąsiada
    for (let i = 0; i < SIL_N; i++) {
      if (!bins[i]) bins[i] = bins[(i + SIL_N - 1) % SIL_N] || bins[(i + 1) % SIL_N] || 1;
    }
    return bins;
  }

  const SIL = {
    Round: measure("GRP_Body_Round"),
    Square: measure("GRP_Body_Square"),
    Heart: measure("GRP_Body_Heart"),
  };

  function silAt(bins, angle) {
    if (!bins) return 1;
    const t = (angle / (Math.PI * 2)) * SIL_N;
    const i = Math.floor(t);
    const f = t - i;
    const a = bins[((i % SIL_N) + SIL_N) % SIL_N];
    const b = bins[(((i + 1) % SIL_N) + SIL_N) % SIL_N];
    return a + (b - a) * f;
  }

  /** Ile razy dalej od osi leży obrys danego kształtu niż obrys koła. */
  function shapeFactor(Shape, angle) {
    if (Shape === "Round" || !SIL[Shape] || !SIL.Round) return 1;
    const base = silAt(SIL.Round, angle);
    return base > 0.001 ? silAt(SIL[Shape], angle) / base : 1;
  }

  /* Pozycje wyjściowe dekoracji zapamiętujemy raz, w układzie modelu - inaczej
     kolejne zmiany kształtu mnożyłyby się jedna przez drugą. Pierścień złota to
     obręcz wokół osi: przesunięcie jej niczego nie naprostuje, więc na
     kwadracie i sercu po prostu jej nie pokazujemy. */
  const decoParts = [];
  let goldRing = null;
  root.updateMatrixWorld(true);
  /* Warianty "_Core" (środek blatu) i "_T2" (wierzch drugiego piętra) też muszą
     trafić na obrys kształtu. Dla piętra działa ten sam współczynnik: piętro jest
     przeskalowaną kopią bryły, więc stosunek obrysu do okręgu jest identyczny. */
  [
    "GRP_Fruits", "GRP_Fruits_Core", "GRP_Fruits_T2",
    "GRP_Gold", "GRP_Gold_T2",
    "GRP_Flowers", "GRP_Flowers_Core", "GRP_Flowers_T2",
  ].forEach(function (name) {
    const g = groups[name];
    if (!g) return;
    g.children.forEach(function (c) {
      if (c.name && c.name.indexOf("GoldRing") === 0) { goldRing = c; return; }
      const p = root.worldToLocal(c.getWorldPosition(new THREE.Vector3()));
      decoParts.push({ obj: c, x: p.x, z: p.z, y: p.y });
    });
  });

  let decoShape = null;
  function fitDeco(Shape) {
    if (Shape === decoShape) return;
    decoShape = Shape;
    if (goldRing) goldRing.visible = Shape === "Round";
    decoParts.forEach(function (d) {
      const k = shapeFactor(Shape, Math.atan2(d.z, d.x));
      tmpV.set(d.x * k, d.y, d.z * k);
      root.localToWorld(tmpV);
      d.obj.parent.worldToLocal(tmpV);
      d.obj.position.copy(tmpV);
    });
  }

  // grupa na sklonowane figurki i napis (poza skalowanym korpusem?
  // nie - wszystko skalujemy razem, jak prawdziwy tort)
  const figLayer = new THREE.Group();
  root.add(figLayer);

  // ── napis: tekstura canvas na okrągłej płytce ──
  const textCanvas = document.createElement("canvas");
  textCanvas.width = 512;
  textCanvas.height = 512;
  const textTex = new THREE.CanvasTexture(textCanvas);
  textTex.colorSpace = THREE.SRGBColorSpace;
  /* Napisy rysujemy materiałem BEZ oświetlenia (MeshBasic). Wcześniejszy
     MeshStandard z metalness 0.3 sprawiał, że tekst mienił się i ciemniał
     zależnie od kąta - częściowy metal bez mapy otoczenia łapie refleksy
     świateł kierunkowych. Napis ma być zawsze jednakowo czytelny. */
  const textMat = new THREE.MeshBasicMaterial({
    map: textTex,
    transparent: true,
  });
  const textMesh = new THREE.Mesh(new THREE.CircleGeometry(0.72, 48), textMat);
  textMesh.rotation.x = -Math.PI / 2;
  textMesh.visible = false;
  root.add(textMesh);

  // ── napis na tabliczce toppera ──
  const plaqueCanvas = document.createElement("canvas");
  plaqueCanvas.width = 512;
  plaqueCanvas.height = 256;
  const plaqueTex = new THREE.CanvasTexture(plaqueCanvas);
  plaqueTex.colorSpace = THREE.SRGBColorSpace;
  const plaqueMat = new THREE.MeshBasicMaterial({
    map: plaqueTex,
    transparent: true,
  });
  // tabliczka w GLB: środek (0, TOP+0.30, 0), front ku kamerze (+Z)
  const PLAQUE_Y = TOP_Y + 0.30;
  const plaqueText = new THREE.Mesh(new THREE.PlaneGeometry(0.56, 0.27), plaqueMat);
  plaqueText.position.set(0, PLAQUE_Y, 0.043);
  plaqueText.visible = false;
  root.add(plaqueText);
  /* Druga strona tabliczki: ta sama tekstura na płaszczyźnie obróconej o 180°
     (obrót, nie skala - tekst czyta się normalnie, nie lustrzanie). Deska po
     eksporcie do glTF zajmuje z od -0.018 do +0.017 (prism w Blenderze rośnie
     w dół od y=0.018), więc tylny napis musi zejść ZA -0.018 - lustrzanie do
     frontu (+0.043) wypada -0.043. Poprzednie -0.007 tkwiło wewnątrz deski. */
  const plaqueBack = new THREE.Mesh(plaqueText.geometry, plaqueMat);
  plaqueBack.rotation.y = Math.PI;
  plaqueBack.position.set(0, PLAQUE_Y, -0.043);
  plaqueBack.visible = false;
  root.add(plaqueBack);

  /* Kopia toppera dla gotowców: GRP_Topper żyje w standardowym korpusie,
     który przy gotowym projekcie gaśnie - klon w root pokazuje patyczki
     z deską także na podmienionym modelu. Wysokości pięter gotowców są
     budowane pod stackTop, więc ten sam lift pasuje bez przeliczeń. */
  const topperClone = groups["GRP_Topper"] ? groups["GRP_Topper"].clone(true) : null;
  if (topperClone) {
    topperClone.visible = false;
    root.add(topperClone);
  }

  /* ── tabliczka z napisem na froncie tortu (krok „Dodatki") ──
     Biały dysk z lukrowym napisem przylegający do ścianki dolnego piętra.
     Promień ścianki różni się między standardem (~1.03) a gotowcami chrzcin
     (0.105 m x SCALE 3.87 = 0.41), więc pozycja i średnica idą za aktywnym
     modelem w apply(). Na sercu wartość kroku chowa metaJson.hideWhen. */
  const wallCanvas = document.createElement("canvas");
  wallCanvas.width = 512;
  wallCanvas.height = 512;
  const wallTex = new THREE.CanvasTexture(wallCanvas);
  wallTex.colorSpace = THREE.SRGBColorSpace;
  const wallMat = new THREE.MeshBasicMaterial({ map: wallTex, transparent: true });
  const wallPlaque = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), wallMat);
  wallPlaque.visible = false;
  root.add(wallPlaque);
  let wallDrawn = null;
  function drawWallPlaque(str) {
    if (wallDrawn === str) return;
    wallDrawn = str;
    const ctx = wallCanvas.getContext("2d");
    ctx.clearRect(0, 0, 512, 512);
    ctx.beginPath();
    ctx.arc(256, 256, 244, 0, 2 * Math.PI);
    ctx.fillStyle = "#fdfaf4";
    ctx.fill();
    ctx.lineWidth = 10;
    ctx.strokeStyle = "#eddac9";
    ctx.stroke();
    ctx.fillStyle = "#8a6a3c";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    /* zawijanie do trzech linii; font maleje, aż najdłuższa linia wejdzie */
    const words = String(str).split(/\s+/).filter(Boolean);
    let size = 96;
    let lines = [str];
    while (size > 28) {
      ctx.font = "italic 600 " + size + "px Georgia, 'Times New Roman', serif";
      lines = [];
      let cur = "";
      for (let i = 0; i < words.length; i++) {
        const t = cur ? cur + " " + words[i] : words[i];
        if (cur && ctx.measureText(t).width > 380) { lines.push(cur); cur = words[i]; }
        else cur = t;
      }
      if (cur) lines.push(cur);
      const fits = lines.length <= 3 && lines.every(function (l) { return ctx.measureText(l).width <= 400; });
      if (fits) break;
      size -= 6;
    }
    const lh = size * 1.15;
    const y0 = 256 - ((lines.length - 1) * lh) / 2;
    lines.forEach(function (l, i) { ctx.fillText(l, 256, y0 + i * lh); });
    wallTex.needsUpdate = true;
  }

  /* ── topper weselny: zlote inicjaly pary na patyczkach ──
     Rysowane dynamicznie z pola "Inicjaly pary" (bez wpisu: A&B), wiec podglad
     pokazuje litery klienta. Cienka plaszczyzna z canvasem odpowiada plaskiemu
     akrylowemu topperowi z referencji; MeshBasic - napisy sie nie mienia. */
  const monoCanvas = document.createElement("canvas");
  monoCanvas.width = 1024;
  monoCanvas.height = 512;
  const monoTex = new THREE.CanvasTexture(monoCanvas);
  monoTex.colorSpace = THREE.SRGBColorSpace;
  const monoMat = new THREE.MeshBasicMaterial({ map: monoTex, transparent: true });
  const monogram = new THREE.Group();
  const monoPlate = new THREE.Mesh(new THREE.PlaneGeometry(0.9, 0.45), monoMat);
  monoPlate.position.y = 0.36;
  monogram.add(monoPlate);
  const monoBack = new THREE.Mesh(monoPlate.geometry, monoMat);
  monoBack.rotation.y = Math.PI;
  monoBack.position.y = 0.36;
  monogram.add(monoBack);
  const stickMat = new THREE.MeshBasicMaterial({ color: 0xa9853f });
  [-0.16, 0.16].forEach(function (x) {
    const s = new THREE.Mesh(new THREE.CylinderGeometry(0.007, 0.007, 0.34, 6), stickMat);
    s.position.set(x, 0.14, 0);
    monogram.add(s);
  });
  monogram.visible = false;
  root.add(monogram);
  let monoDrawn = null;
  function drawMonogram(str) {
    if (monoDrawn === str) return;
    monoDrawn = str;
    const ctx = monoCanvas.getContext("2d");
    ctx.clearRect(0, 0, 1024, 512);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    let size = 300;
    ctx.font = "italic 500 " + size + "px Georgia, 'Times New Roman', serif";
    while (ctx.measureText(str).width > 940 && size > 60) {
      size -= 10;
      ctx.font = "italic 500 " + size + "px Georgia, 'Times New Roman', serif";
    }
    /* pionowy gradient udaje polysk zlota bez udzialu swiatel sceny */
    const g = ctx.createLinearGradient(0, 256 - size * 0.55, 0, 256 + size * 0.55);
    g.addColorStop(0, "#ecd08a");
    g.addColorStop(0.5, "#caa250");
    g.addColorStop(1, "#a67c2e");
    ctx.fillStyle = g;
    ctx.fillText(str, 512, 256);
    monoTex.needsUpdate = true;
  }

  /* ── topper weselny: figurki pary mlodej (asset Hyper3D, ladowany leniwie) ── */
  let coupleNode = null;
  let coupleLoading = null;
  function ensureCouple() {
    if (coupleNode) return Promise.resolve(coupleNode);
    if (coupleLoading) return coupleLoading;
    const url = host.dataset.topperCouple;
    if (!url) return Promise.resolve(null);
    coupleLoading = new GLTFLoader().loadAsync(url).then(function (g) {
      coupleNode = g.scene;
      coupleNode.visible = false;
      root.add(coupleNode);
      return coupleNode;
    }).catch(function () {
      /* brak assetu nie moze wysypac podgladu - po prostu bez figurek */
      coupleLoading = null;
      return null;
    });
    return coupleLoading;
  }

  /* ── toppery z gotowymi wzorami (chrzciny/komunia): dedykowane GLB ──
     Wybrany wzor niesie metaJson.kind ("ihs" / "chrzest"); "monogram"
     korzysta z rysowanych zlotych liter jak topper slubny. GLB laduja sie
     leniwie i mieszkaja w root, wiec dzialaja tez na gotowcach. */
  const wzorNodes = {};
  const wzorLoading = {};
  function ensureWzor(kind) {
    if (wzorNodes[kind]) return Promise.resolve(wzorNodes[kind]);
    if (wzorLoading[kind]) return wzorLoading[kind];
    const url = kind === "ihs" ? host.dataset.topperIhs
      : kind === "chrzest" ? host.dataset.topperChrzest : null;
    if (!url) return Promise.resolve(null);
    wzorLoading[kind] = new GLTFLoader().loadAsync(url).then(function (g) {
      const node = g.scene;
      node.visible = false;
      root.add(node);
      wzorNodes[kind] = node;
      delete wzorLoading[kind];
      return node;
    }).catch(function () {
      /* brak assetu nie moze wysypac podgladu - po prostu bez toppera */
      wzorLoading[kind] = null;
      return null;
    });
    return wzorLoading[kind];
  }

  function drawPlaque(str) {
    const ctx = plaqueCanvas.getContext("2d");
    ctx.clearRect(0, 0, 512, 256);
    if (str) {
      ctx.fillStyle = "#8a6a3c";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      let size = 84;
      ctx.font = "italic 600 " + size + "px Georgia, 'Times New Roman', serif";
      while (ctx.measureText(str).width > 440 && size > 26) {
        size -= 5;
        ctx.font = "italic 600 " + size + "px Georgia, 'Times New Roman', serif";
      }
      ctx.fillText(str, 256, 132);
    }
    plaqueTex.needsUpdate = true;
  }

  /* Od 8 znaków napis łamie się na dwie linie - w spacji najbliższej środka,
     a pojedyncze długie słowo w połowie. Jedna linia zmuszała długie napisy
     do drobnego fontu; dwie krótsze linie mieszczą się większym. */
  function splitTwo(str) {
    if (str.length < 8) return [str];
    const mid = str.length / 2;
    let best = -1;
    for (let i = 1; i < str.length - 1; i++) {
      if (str[i] === " " && (best < 0 || Math.abs(i - mid) < Math.abs(best - mid))) best = i;
    }
    if (best < 0) {
      const cut = Math.ceil(str.length / 2);
      return [str.slice(0, cut), str.slice(cut)];
    }
    return [str.slice(0, best), str.slice(best + 1)];
  }

  function drawText(str, color) {
    const ctx = textCanvas.getContext("2d");
    ctx.clearRect(0, 0, 512, 512);
    if (str) {
      ctx.fillStyle = color || "#b89460";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const lines = splitTwo(str);
      let size = lines.length > 1 ? 88 : 97;
      const fits = function () {
        return lines.every(function (l) { return ctx.measureText(l).width <= 430; });
      };
      ctx.font = "italic 600 " + size + "px Georgia, 'Times New Roman', serif";
      while (!fits() && size > 30) {
        size -= 6;
        ctx.font = "italic 600 " + size + "px Georgia, 'Times New Roman', serif";
      }
      ctx.shadowColor = "rgba(0,0,0,.35)";
      ctx.shadowBlur = 4;
      ctx.shadowOffsetY = 2;
      if (lines.length === 1) {
        ctx.fillText(lines[0], 256, 262);
      } else {
        const gap = size * 0.58;
        ctx.fillText(lines[0], 256, 262 - gap);
        ctx.fillText(lines[1], 256, 262 + gap);
      }
    }
    textTex.needsUpdate = true;
  }

  // ── napis na boku: zakrzywiona taśma wokół frontu tortu ──
  const sideCanvas = document.createElement("canvas");
  sideCanvas.width = 1024;
  sideCanvas.height = 256;
  const sideTex = new THREE.CanvasTexture(sideCanvas);
  sideTex.colorSpace = THREE.SRGBColorSpace;
  const sideMat = new THREE.MeshBasicMaterial({
    map: sideTex,
    transparent: true,
    side: THREE.FrontSide,
  });
  const SIDE_ARC = 1.5; // ~86 stopni wokół frontu
  const sideText = new THREE.Mesh(
    new THREE.CylinderGeometry(1.035, 1.035, 0.34, 48, 1, true, -SIDE_ARC / 2, SIDE_ARC),
    sideMat,
  );
  sideText.position.set(0, 0.36, 0);
  sideText.visible = false;
  root.add(sideText);

  function drawSide(str, color) {
    const ctx = sideCanvas.getContext("2d");
    ctx.clearRect(0, 0, 1024, 256);
    if (str) {
      ctx.fillStyle = color || "#b89460";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      let size = 97;
      ctx.font = "italic 600 " + size + "px Georgia, 'Times New Roman', serif";
      while (ctx.measureText(str).width > 640 && size > 30) {
        size -= 6;
        ctx.font = "italic 600 " + size + "px Georgia, 'Times New Roman', serif";
      }
      ctx.shadowColor = "rgba(0,0,0,.3)";
      ctx.shadowBlur = 4;
      ctx.shadowOffsetY = 2;
      ctx.fillText(str, 512, 132);
    }
    sideTex.needsUpdate = true;
  }

  // ── gradient ombre na kremie ──
  let ombreTex = null;
  function ombreTexture(hex) {
    const c = document.createElement("canvas");
    c.width = 4;
    c.height = 256;
    const x = c.getContext("2d");

    /* Ombre idzie od koloru do CZYSTEJ BIELI. Wcześniej biegło od base*0,75 do
       lerp(base, biel, 0,15), czyli w praktyce było samym przyciemnieniem dołu -
       bieli nie dotykało wcale i przy pastelowym kremie nie było go widać.

       Dół pogłębiamy wyłącznie na jasności, w HSL (l*0,78 - na prośbę klienta
       ciemniej niż pierwotne l*0,9). Podbijanie nasycenia wypróbowałem i odpadło:
       blady róż #f2c9d4 robił się u dołu #ee517b, czyli innym kolorem niż
       wybrany. Odcień i nasycenie zostają nietknięte - klient ma zobaczyć SWÓJ
       kolor przechodzący w biel. */
    const base = new THREE.Color(hex);
    const hsl = { h: 0, s: 0, l: 0 };
    base.getHSL(hsl);
    const deep = new THREE.Color().setHSL(hsl.h, hsl.s, Math.max(0.1, hsl.l * 0.78));
    const white = new THREE.Color(0xffffff);

    /* Cztery przystanki, nie dwa. Liniowe przejście do bieli wypłukuje kolor już
       w połowie wysokości i ombre wygląda na wyblakłe; tutaj kolor trzyma dolną
       połowę, a biel wchodzi dopiero u samej góry. */
    const g = x.createLinearGradient(0, c.height, 0, 0);
    g.addColorStop(0.0, "#" + deep.getHexString());
    g.addColorStop(0.45, "#" + deep.clone().lerp(white, 0.16).getHexString());
    g.addColorStop(0.78, "#" + deep.clone().lerp(white, 0.58).getHexString());
    g.addColorStop(1.0, "#ffffff");
    x.fillStyle = g;
    x.fillRect(0, 0, c.width, c.height);

    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    /* KIERUNEK: geometria przyszła z GLTFLoadera, a on trzyma konwencję glTF
       (v=0 to góra obrazu) i swoim teksturom ustawia flipY=false. CanvasTexture
       ma jednak domyślnie flipY=true - obraz wgrywał się odwrócony i kolor
       lądował U GÓRY tortu, a biel u dołu. Bez tej linii gradient stoi na
       głowie, niezależnie od kolejności przystanków w canvasie. */
    t.flipY = false;
    return t;
  }

  const clones = [];

  /* Kadrowanie zależy od wysokości tortu: cztery piętra są prawie trzy razy
     wyższe niż jedno i przy stałej kamerze wyjeżdżają za kadr. Przeliczamy
     tylko przy zmianie wysokości, żeby nie walczyć z orbitowaniem myszką -
     kierunek patrzenia klienta zostaje, zmienia się dystans i punkt celowania. */
  const CAM_DIST = camera.position.length();
  let framedFor = null;
  let framedZoom = null;
  function frame(topY, zoom) {
    if (framedFor !== null && Math.abs(framedFor - topY) < 0.001 && framedZoom === zoom) return;
    framedFor = topY;
    framedZoom = zoom;
    const dir = camera.position.clone().sub(controls.target).normalize();
    controls.target.set(0, 0.12 + topY * 0.49, 0);
    /* `zoom` < 1: torty liczone waga (weselny) stoja blizej kamery - kadr i tak
       jest kwantowany do liczby pieter, wiec zapas na wahania skali zbedny;
       gotowiec z wlasnym modelem podjezdza jeszcze blizej, zeby bylo widac
       dekoracje (liscie, owoce), a nie sylwetke z daleka */
    camera.position
      .copy(controls.target)
      .addScaledVector(dir, CAM_DIST * (1 + Math.max(0, topY - TOP_Y) * 0.42) * zoom);
    controls.update();
  }

  // ── aplikowanie stanu ──
  function apply(st) {
    st = st || {};
    lastState = st;
    /* gotowy projekt z własnym modelem? (metaJson.model wybranej wartości) */
    let altKind = null;
    Object.keys(st).forEach(function (k) {
      const v = st[k];
      if (v && !Array.isArray(v) && v.meta && v.meta.model && altUrls[v.meta.model]) altKind = v.meta.model;
    });
    const shapeMeta = st.ks && st.ks.meta ? st.ks.meta : {};
    const shape = (shapeMeta.shape || "round").toLowerCase();
    const Shape = shape.charAt(0).toUpperCase() + shape.slice(1);
    const styleMeta = st.st && st.st.meta ? st.st.meta : {};
    const style = styleMeta.style || "smooth";
    const ppl = typeof st.os === "number" ? st.os : null;
    const naked = style === "naked";
    /* Liczba pięter przychodzi z konfiguratora (`_size.tiers`), który liczy ją
       z progów wagowych zestawu opcji. Zestaw bez progów - tort urodzinowy -
       tego pola nie przysyła, więc zostaje stara reguła: powyżej 35 osób dwa
       piętra. Dzięki temu podgląd nie zależy od tego, w jakiej jednostce dany
       tort mierzy rozmiar. */
    const size = st._size || null;
    /* Przed wyborem rozmiaru pokazujemy minimum pięter zestawu opcji
       (`_tiersMin` z progów wagowych - tort weselny startuje jako pusty
       2-piętrowy); atrybut sekcji zostaje jako awaryjne nadpisanie. */
    const defTiers = st._tiersMin || parseInt(host.dataset.tiersDefault, 10) || 1;
    const tiers =
      size && size.tiers
        ? Math.max(1, Math.min(MAX_TIERS, size.tiers))
        : !!ppl && ppl > 35
          ? 2
          : defTiers;
    const top = stackTop(tiers);
    const lift = top - TOP_Y; // o ile wierzch tortu jest wyżej niż sam korpus

    // korpusy + piętra (piętra zawsze w kształcie wybranym przez klienta)
    ["Round", "Square", "Heart"].forEach(function (s) {
      if (groups["GRP_Body_" + s]) groups["GRP_Body_" + s].visible = !naked && s === Shape;
      if (groups["GRP_Naked_" + s]) groups["GRP_Naked_" + s].visible = naked && s === Shape;
      if (groups["GRP_Drip_" + s]) groups["GRP_Drip_" + s].visible = style === "drip" && s === Shape;
      /* polewa wierzchu drugiego piętra - tylko przy dokładnie dwóch piętrach,
         jak _T2 przy owocach i kwiatach (przy trzech zasłania ją piętro trzecie) */
      if (groups["GRP_DripT2_" + s])
        groups["GRP_DripT2_" + s].visible = style === "drip" && s === Shape && tiers === 2;
      /* kaskady złota na ściankach są budowane per kształt, wprost na obrysie -
         fitDeco ich nie dotyka (blaty i perły zostają w GRP_Gold/_T2) */
      if (groups["GRP_GoldWall_" + s])
        groups["GRP_GoldWall_" + s].visible = style === "gold" && s === Shape;
      if (groups["GRP_GoldWallT2_" + s])
        groups["GRP_GoldWallT2_" + s].visible = style === "gold" && s === Shape && tiers === 2;
      for (let t = 2; t <= MAX_TIERS; t++) {
        /* przy stylu naked piętra też są naked - kremowe gasną */
        const g = groups["GRP_Tier" + t + "_" + s];
        if (g) g.visible = !naked && s === Shape && t <= tiers;
        const ng = groups["GRP_NakedTier" + t + "_" + s];
        if (ng) ng.visible = naked && s === Shape && t <= tiers;
      }
    });
    // starszy GLB miał jedno wspólne górne piętro, bez wariantów kształtu
    if (groups["GRP_Tier2"]) groups["GRP_Tier2"].visible = tiers >= 2;
    fitDeco(Shape); // dekoracje na obrys wybranego kształtu
    /* Dekoracja wierzchu zależy od liczby pięter. Środek blatu ("_Core") gaśnie,
       gdy staje na nim drugie piętro - inaczej owoce i kwiaty przy jego krawędzi
       przebijałyby ściankę. Wariant "_T2" leży na wierzchu drugiego piętra, więc
       ma sens tylko przy dokładnie dwóch (przy trzech zasłania go piętro trzecie;
       tort weselny dostanie własne warianty, gdy się nim zajmiemy). */
    const onBase = tiers === 1;
    const onTier2 = tiers === 2;
    if (groups["GRP_Gold"]) groups["GRP_Gold"].visible = style === "gold";
    /* zloto drugiego pietra - tylko przy dokladnie dwoch pietrach, jak inne _T2 */
    if (groups["GRP_Gold_T2"])
      groups["GRP_Gold_T2"].visible = style === "gold" && onTier2;
    [
      ["GRP_Fruits", "fruits"],
      ["GRP_Flowers", "flowers"],
    ].forEach(function (pair) {
      const base = groups[pair[0]];
      const core = groups[pair[0] + "_Core"];
      const t2 = groups[pair[0] + "_T2"];
      const on = style === pair[1];
      if (base) base.visible = on;
      if (core) core.visible = on && onBase;
      if (t2) t2.visible = on && onTier2;
    });

    /* Skala: niejednorodna - wszerz bardziej niż w górę. Tort liczony wagą
       (weselny) rośnie z kilogramów; przy liczonym osobami zostaje stara
       krzywa, więc podgląd urodzinowego nie zmienia się ani o piksel.
       Rozpoznajemy to po `_size.tiers`, czyli po tym, że zestaw opcji w ogóle
       definiuje progi wagowe. */
    const s =
      size && size.tiers && typeof size.kg === "number" && size.kg > 0
        ? Math.max(0.8, Math.min(1.2, 0.78 + size.kg * 0.016))
        : ppl
          ? Math.max(0.8, Math.min(1.2, 0.78 + ppl * 0.011))
          : 0.92;
    root.scale.set(s, 1 + (s - 1) * 0.45, s);

    /* Powłoka tortu: kolor bierze się z osobnego kroku („Kolor tortu", klucz
       `kol`), a nie ze smaku kremu - klient wybiera smak niezależnie od tego,
       jak tort ma wyglądać. Bez wyboru zostaje biel.
       Zestawy opcji bez tego kroku (starsze zapisy w bazie) w ogóle nie
       przysyłają klucza - wtedy zostaje stare zachowanie, czyli barwienie
       kremem, żeby podgląd nie zbielał z dnia na dzień.
       „Inny" nie ma czego pokazać: jego `color` to gradient na kafelek, więc
       do materiału trafia tylko wartość zaczynająca się od `#`. */
    if (mats.Cream) {
      const hasColorStep = st.kol !== undefined;
      const cm = (hasColorStep ? st.kol && st.kol.meta : st.k && st.k.meta) || {};
      const coat = typeof cm.color === "string" && cm.color.charAt(0) === "#" ? cm.color : "#ffffff";
      mats.Cream.color.set(coat);
      mats.Cream.roughness = cm.rough != null ? cm.rough : 0.45;
      if (style === "ombre") {
        if (ombreTex) ombreTex.dispose();
        ombreTex = ombreTexture(coat);
        mats.Cream.map = ombreTex;
        mats.Cream.color.set("#ffffff");
      } else if (mats.Cream.map) {
        mats.Cream.map = null;
      }
      mats.Cream.needsUpdate = true;
      /* Powłoki modeli alternatywnych (BohoCream itd.) - ten sam kolor kroku
         „Kolor tortu"; roughness zostaje z pliku, bo niesie fakturę szpachli.
         Materiały „...CreamLight" (kontrastowe piętra pastelowego króliczka)
         dostają JAŚNIEJSZY odcień wybranego koloru - dwutonowy układ zostaje,
         ale każde piętro reaguje na wybór (feedback: „środkowe się nie
         zmienia"). */
      altCreams.forEach(function (m) {
        if (/creamlight/i.test(m.name || "")) {
          const c = new THREE.Color(coat);
          c.lerp(new THREE.Color("#ffffff"), 0.62);
          m.color.copy(c);
        } else {
          m.color.set(coat);
        }
        m.needsUpdate = true;
      });
    }
    // biszkopt
    if (mats.Sponge && st.b && st.b.meta && st.b.meta.color) mats.Sponge.color.set(st.b.meta.color);
    // nadzienia
    const fills = Array.isArray(st.n) ? st.n : [];
    if (mats.Filling1 && fills[0] && fills[0].meta && fills[0].meta.color) mats.Filling1.color.set(fills[0].meta.color);
    if (mats.Filling2) {
      const f2 = fills[1] || fills[0];
      if (f2 && f2.meta && f2.meta.color) mats.Filling2.color.set(f2.meta.color);
    }

    /* topper: `meta.topper` bywa boolem (urodzinowy - tabliczka w GLB) albo
       stringiem (weselny: "monogram" = zlote inicjaly, "couple" = figurki
       mlodych). Monogram i figurki dzialaja tez na gotowcach - nie naleza do
       standardowego korpusu, tylko stoja na wierzchu wg stackTop. */
    /* krok Dodatki bywa multi (checkbox chrzcin/komunii: topper + tabliczka
       NARAZ) albo pojedynczy (weselne radio) - normalizujemy do listy */
    const tSel = Array.isArray(st.t) ? st.t : st.t ? [st.t] : [];
    let topperKind = false;
    tSel.forEach(function (v) {
      if (v && v.meta && v.meta.topper) topperKind = v.meta.topper;
    });
    /* gotowy wzor toppera (radio `wt`, chrzciny/komunia) podmienia bool na
       konkretny rodzaj: "ihs"/"chrzest" = dedykowany GLB, "monogram" =
       zlote litery z pola tekstowego (mechanizm slubny) */
    if (topperKind === true && st.wt && !Array.isArray(st.wt) && st.wt.meta && st.wt.meta.kind) {
      topperKind = st.wt.meta.kind;
    }
    const topperOn = topperKind === true;
    if (groups["GRP_Topper"]) {
      groups["GRP_Topper"].visible = topperOn;
      groups["GRP_Topper"].position.y = lift;
    }
    const plaqueStr = topperOn && typeof st.nt === "string" ? st.nt.trim() : "";
    plaqueText.visible = !!plaqueStr;
    plaqueBack.visible = !!plaqueStr;
    if (plaqueStr) {
      drawPlaque(plaqueStr);
      plaqueText.position.y = PLAQUE_Y + lift;
      plaqueBack.position.y = PLAQUE_Y + lift;
    }

    // figurki - instancje sylwetek rozmieszczone na wierzchu
    clones.forEach(function (c) { figLayer.remove(c); });
    clones.length = 0;
    const nFig = typeof st.f === "number" ? st.f : 0;
    if (nFig > 0 && figureTemplates.length) {
      // promień wierzchu - czwarte piętro jest wąskie, figurki muszą się na nim zmieścić
      const topR = TIER_R[Math.min(tiers, MAX_TIERS) - 1];
      for (let i = 0; i < Math.min(nFig, 12); i++) {
        const tpl = figureTemplates[i % figureTemplates.length];
        const c = tpl.clone(true);
        c.visible = true;
        const onUpper = tiers > 1 && i < 4;
        const a = (i * 2.399) + 0.7; // złoty kąt - równomiernie bez kolizji
        // ten sam obrys co dla dekoracji: na kwadracie i sercu wierzch sięga dalej
        const ring = (onUpper ? topR * 0.5 : i < 6 ? 0.5 : 0.78) * shapeFactor(Shape, a);
        c.position.set(Math.cos(a) * ring, onUpper ? lift : 0, Math.sin(a) * ring);
        c.rotation.y = -a + Math.PI / 2;
        clones.push(c);
        figLayer.add(c);
      }
    }

    // napis na torcie: pozycja (góra/bok), kolor, unik toppera z przodu
    const napis = typeof st.na === "string" ? st.na.trim() : "";
    const napisPos = st.np && st.np.meta && st.np.meta.pos === "side" ? "side" : "top";
    const napisCol = (st.nk && st.nk.meta && st.nk.meta.color) || "#b89460";
    textMesh.visible = !!napis && napisPos === "top";
    sideText.visible = !!napis && napisPos === "side";
    if (textMesh.visible) {
      drawText(napis, napisCol);
      /* Przy dripie tafla polewy siega +0.022 nad blat (pietro: +0.016)
         i napis na +0.012 tonal w czekoladzie. Nad polewa musi byc wyzej. */
      textMesh.position.y = top + (style === "drip" ? 0.032 : 0.012);
      /* Napis na wierzchu musi zmieścić się na najwyższym piętrze, a przy
         topperze zejść na jego przód, żeby nie wchodził pod tabliczkę. */
      const fit = tiers > 1 ? TIER_R[Math.min(tiers, MAX_TIERS) - 1] / TIER_R[1] : 1;
      const tScale = (topperOn ? 0.68 : 1) * fit;
      textMesh.position.z = topperOn ? (tiers > 1 ? 0.2 : 0.42) * fit : 0;
      textMesh.scale.set(tScale, tScale, tScale);
    }
    if (sideText.visible) {
      drawSide(napis, napisCol);
      sideText.position.y = 0.36;
    }

    /* ── podmiana modelu dla gotowych projektów z własnym GLB (np. boho) ──
       Gdy docelowy wariant jest już w pamięci - pokazujemy go od razu. Gdy
       dopiero się ładuje - NIE ruszamy sceny (zostaje poprzedni wariant boho
       albo standard), żeby zmiana rozmiaru nie migała standardowym tortem.
       Po załadowaniu ensureAlt przepuszcza stan jeszcze raz i wszystko się
       domyka. */
    /* zakres pieter ograniczamy do wariantow, ktore dany model faktycznie ma
       (weselne: t2-t4, chrzest: t1-t2) - wczesniejsze sztywne 2..4 pokazaloby
       jednopietrowy chrzest jako dwupietrowy */
    let altTiers = Math.min(Math.max(tiers, 2), 4);
    if (altKind) {
      const avail = Object.keys(altUrls[altKind]).filter(function (k) { return altUrls[altKind][k]; }).map(Number);
      if (avail.length) altTiers = Math.min(Math.max(tiers, Math.min.apply(0, avail)), Math.max.apply(0, avail));
    }
    const wantKey = altKind ? altKey(altKind, altTiers) : null;
    altWanted = wantKey;
    if (!wantKey) {
      Object.keys(altCache).forEach(function (k) { altCache[k].visible = false; });
      stdRoot.visible = true;
    } else if (altCache[wantKey]) {
      Object.keys(altCache).forEach(function (k) { altCache[k].visible = k === wantKey; });
      stdRoot.visible = false;
    } else {
      ensureAlt(altKind, altTiers).then(function (node) {
        if (node && altWanted === wantKey) apply(lastState);
      });
    }
    /* Topper na gotowcu: GRP_Topper należy do standardowego korpusu, więc na
       podmienionym modelu pokazuje go klon. Bez klona (starszy GLB) napis
       toppera zgasłby jak dawniej, żeby nie wisiał w powietrzu. */
    if (topperClone) {
      topperClone.visible = topperOn && wantKey && !stdRoot.visible ? true : false;
      topperClone.position.y = lift;
    }
    if (wantKey && !stdRoot.visible && !(topperOn && topperClone)) {
      plaqueText.visible = false;
      plaqueBack.visible = false;
    }

    /* ── tabliczka z napisem na froncie (meta.plaque na wartości kroku
       „Dodatki" + tekst z pola `tb`) - działa na standardzie i gotowcach ── */
    const plaqueSel = tSel.some(function (v) { return v && v.meta && v.meta.plaque; });
    const tbStr = plaqueSel && typeof st.tb === "string" ? st.tb.trim() : "";
    const altShown = !!wantKey && !stdRoot.visible;
    /* Dysk pokazuje sie od razu po wyborze Tabliczki - pusty, napis dochodzi
       w miare pisania. Wisi w kierunku DOMYSLNEGO AZYMUTU KAMERY (CAM_AZIM),
       tak jak toppery - inaczej topper patrzyl w kamere, a tabliczka w bok. */
    const CAM_AZIM = 0.68; // domyslny azymut kamery (~39 stopni), wspolny dla ozdob
    wallPlaque.visible = plaqueSel;
    if (plaqueSel) {
      drawWallPlaque(tbStr);
      let wallR = altShown ? 0.425 : 1.05 * shapeFactor(Shape, Math.PI / 2 - CAM_AZIM);
      let plY = altShown ? 0.33 : 0.36;
      let plD = altShown ? 0.42 : 0.78;
      /* Spiacy maluszek: falbana platkow zaslania dolne pietro - tabliczka
         idzie na DRUGIE pietro od dolu (r 0.085-0.086 x SCALE ~0.33; srodek
         pietra wg wysokosci wariantu), a przy t1 w wolny pas nad falbana. */
      if (altShown && altKind === "bobas") {
        if (altTiers >= 2) {
          wallR = 0.34;
          plY = altTiers === 2 ? 0.81 : 0.77;
        } else {
          plY = 0.47;
          plD = 0.28;
        }
      }
      wallPlaque.scale.set(plD, plD, 1);
      wallPlaque.position.set(wallR * Math.sin(CAM_AZIM), plY, wallR * Math.cos(CAM_AZIM));
      wallPlaque.rotation.y = CAM_AZIM;
    }

    /* ── ozdoby wierzchu (dzialaja na standardzie i na gotowcach);
       CAM_AZIM zdefiniowany wyzej przy tabliczce ── */
    const topR2 = TIER_R[Math.min(tiers, MAX_TIERS) - 1];
    /* toppery z gotowymi wzorami (dedykowane GLB: IHS, Chrzest Swiety) */
    Object.keys(wzorNodes).forEach(function (k) {
      if (wzorNodes[k]) wzorNodes[k].visible = false;
    });
    if (topperKind === "ihs" || topperKind === "chrzest") {
      const wn = wzorNodes[topperKind];
      if (wn) {
        wn.visible = true;
        const ws = Math.max(0.6, Math.min(1.0, topR2 / 0.45));
        wn.scale.set(ws, ws, ws);
        wn.position.y = top;
        wn.rotation.y = CAM_AZIM;
      } else {
        ensureWzor(topperKind).then(function (n) {
          if (n && lastState) apply(lastState);
        });
      }
    }
    /* monogram: slubny (t.meta.topper="monogram") pokazuje placeholder A&B,
       „Wlasny napis" z wzorow (wt.meta.kind="monogram") czeka na tekst */
    const viaWzor = !!(st.wt && !Array.isArray(st.wt) && st.wt.meta && st.wt.meta.kind);
    const monoTxt = (typeof st.nt === "string" && st.nt.trim()) || "";
    monogram.visible = topperKind === "monogram" && (!viaWzor || !!monoTxt);
    if (monogram.visible) {
      drawMonogram(monoTxt || "A&B");
      const ms = Math.max(0.6, Math.min(1.0, topR2 / 0.45));
      monogram.scale.set(ms, ms, ms);
      monogram.position.y = top - 0.02;
      monogram.rotation.y = CAM_AZIM;
    }
    if (topperKind === "couple") {
      if (coupleNode) {
        coupleNode.visible = true;
        /* figurki maja byc dodatkiem, nie dominowac tortu - ~40% wysokosci
           gornego pietra (uwaga klienta: pelna skala byla za duza) */
        const cs = Math.max(0.52, Math.min(0.75, topR2 / 0.65));
        coupleNode.scale.set(cs, cs, cs);
        coupleNode.position.y = top;
        coupleNode.rotation.y = CAM_AZIM;
      } else {
        ensureCouple().then(function (n) {
          if (n && lastState) apply(lastState);
        });
      }
    } else if (coupleNode) {
      coupleNode.visible = false;
    }

    /* Kadrowanie: tort wazony (weselny, `_size.tiers`) dostaje kadr SKOKOWY -
       liczony tylko z liczby pieter (wysokosc najwyzszego wariantu skali),
       wiec suwak rozmiaru nie buja kamera; przy urodzinowym zostaje plynne
       dopasowanie do wysokosci, bo tam skala zmienia sie rzadziej. */
    const weightBased = !!(size && size.tiers) || !!st._tiersMin;
    frame(
      weightBased ? stackTop(tiers) * 1.09 : top * (1 + (s - 1) * 0.45),
      weightBased ? 0.85 : 1,
    );
    needsRender = true;
  }

  document.addEventListener("cake:state", function (e) { apply(e.detail); });
  apply(null);

  // ── rozmiar / pętla ──
  function resize() {
    const w = host.clientWidth || 300;
    const h = host.clientHeight || 300;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
    needsRender = true;
  }
  new ResizeObserver(resize).observe(host);
  resize();

  /* Render NA ZADANIE, nie w kazdej klatce: stala petla trzymala GPU pod
     pelnym obciazeniem takze wtedy, gdy nic sie nie dzialo (zgloszony lag,
     najbardziej odczuwalny przy gotowcach z gestsza geometria). Klatke rysuje
     zmiana stanu (apply/frame/resize), orbitowanie i wygaszanie dampingu -
     controls.update() zwraca true, dopoki kamera faktycznie sie rusza. */
  controls.addEventListener("change", function () { needsRender = true; });
  renderer.setAnimationLoop(function () {
    if (controls.update()) needsRender = true;
    if (!needsRender) return;
    needsRender = false;
    renderer.render(scene, camera);
  });
}
